/**
 * Champ — ce qu'on tape : un `<input>` (ou un `<textarea>` avec `lignes`) dans
 * la surface commune de « ce qu'on tape » (champs.css) : creusée, hairline, qui
 * s'allume à la marque quand on y est, rouge quand c'est faux.
 *
 *   <Champ etiquette="Pseudo" placeholder="trista" icone={<AtSign />} aide="Sans le @." />
 *   <Champ etiquette="Bio" lignes={3} value={bio} onChange={(e) => setBio(e.target.value)} />
 *   <Champ pilule icone={<Search />} placeholder="Rechercher" taille="sm" />
 *
 * Il prend TOUT ce qu'un `<input>` prend (`value`, `onChange`, `placeholder`,
 * `type`, `inputMode`, `ref`…) : remplacer `<input className={CHAMP} …>` par
 * `<Champ …>` suffit. Autour : une étiquette (`<label>` reliée par `htmlFor`),
 * une aide dessous, une erreur qui la remplace (et pose `aria-invalid`), une
 * icône à gauche, un suffixe à droite (« € », un bouton).
 *
 * Successeur des neuf constantes `CHAMP` / `INPUT_CLS` / `SEARCH_INPUT` du
 * cockpit (171 fichiers avec un `<input>` à eux, 48 avec un `<textarea>`).
 * `className` va au CADRE (largeur, marges), pas à l'input : c'est le cadre
 * qui a une taille.
 */
import {
  useId,
  type ChangeEventHandler,
  type ClipboardEventHandler,
  type FocusEventHandler,
  type FormEventHandler,
  type InputHTMLAttributes,
  type KeyboardEventHandler,
  type ReactNode,
  type Ref,
  type TextareaHTMLAttributes,
} from "react";

import { cn } from "./utils";
import "./champs.css";

export type TailleChamp = "sm" | "md" | "lg";

type Saisie = HTMLInputElement | HTMLTextAreaElement;

export interface ChampProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    "size" | "className" | "children" | "onChange" | "onBlur" | "onFocus" | "onKeyDown" | "onKeyUp" | "onInput" | "onPaste"
  > {
  /** Au-dessus du champ, en capitales espacées (Aeonik Fono). */
  etiquette?: ReactNode;
  /** Dessous, en gris. */
  aide?: ReactNode;
  /** Dessous, en rouge, à la place de l'aide ; le cadre passe au rouge et l'input est `aria-invalid`. */
  erreur?: ReactNode;
  /** Une icône de `@dozka/ui/icons`, à gauche. */
  icone?: ReactNode;
  /** À droite : une unité (« € »), un bouton `ghost` `sm`. */
  suffixe?: ReactNode;
  /** sm 32 px · md 40 px (défaut) · lg 48 px. */
  taille?: TailleChamp;
  /** Une pilule (la recherche). */
  pilule?: boolean;
  /** Un nombre de lignes : le champ devient un `<textarea>` (hauteur libre, redimensionnable). */
  lignes?: number;
  /** La classe du CADRE (largeur, marges) — `w-full`, `max-w-xs`. */
  className?: string;
  ref?: Ref<Saisie>;
  onChange?: ChangeEventHandler<Saisie>;
  onBlur?: FocusEventHandler<Saisie>;
  onFocus?: FocusEventHandler<Saisie>;
  onKeyDown?: KeyboardEventHandler<Saisie>;
  onKeyUp?: KeyboardEventHandler<Saisie>;
  onInput?: FormEventHandler<Saisie>;
  onPaste?: ClipboardEventHandler<Saisie>;
}

/** Les tailles de « ce qu'on tape », partagées avec le Select. */
export const TAILLES_CHAMP: Record<TailleChamp, { cadre: string; hauteur: string; lignes: string; arrondi: string }> = {
  sm: { cadre: "gap-2 px-3 text-[12px]", hauteur: "h-8", lignes: "py-1.5", arrondi: "ch-arrondi-sm" },
  md: { cadre: "gap-2.5 px-3.5 text-[14px]", hauteur: "h-10", lignes: "py-2.5", arrondi: "ch-arrondi" },
  lg: { cadre: "gap-3 px-4 text-[16px]", hauteur: "h-12", lignes: "py-3", arrondi: "ch-arrondi" },
};

/** Le texte sous un champ : l'aide en gris, l'erreur en rouge. */
export function SousLeChamp({ id, aide, erreur }: { id: string; aide?: ReactNode; erreur?: ReactNode }) {
  const texte = erreur ?? aide;
  if (texte === undefined || texte === null || texte === false) return null;
  return (
    <p id={id} className={cn("text-[11.5px] leading-snug", erreur ? "text-erreur" : "text-faint")}>
      {texte}
    </p>
  );
}

export function Champ({
  etiquette,
  aide,
  erreur,
  icone,
  suffixe,
  taille = "md",
  pilule = false,
  lignes,
  className,
  ref,
  id: idDonne,
  disabled,
  ...reste
}: ChampProps) {
  const genere = useId();
  const id = idDonne ?? genere;
  const idAide = `${id}-aide`;
  const t = TAILLES_CHAMP[taille];
  const multiligne = lignes !== undefined;
  const aUnTexte = (erreur ?? aide) !== undefined;

  const communs = {
    id,
    className: "ch-saisie",
    disabled,
    "aria-invalid": erreur ? true : undefined,
    "aria-describedby": aUnTexte ? idAide : undefined,
  } as const;

  return (
    <div className={cn("flex min-w-0 flex-col gap-1.5", className)}>
      {etiquette !== undefined && (
        <label htmlFor={id} className="ch-etiquette">
          {etiquette}
        </label>
      )}
      <div
        className={cn(
          "ch-surface flex items-center",
          pilule ? "ch-pilule" : t.arrondi,
          t.cadre,
          multiligne ? cn(t.lignes, "items-start") : t.hauteur,
        )}
        data-eteint={disabled || undefined}
        data-erreur={erreur ? true : undefined}
      >
        {icone !== undefined && <span className={cn("ch-icone shrink-0 text-faint", multiligne && "mt-[0.2em]")}>{icone}</span>}
        {multiligne ? (
          // Les attributs sont ceux d'un <input> (le contrat du composant) ; un <textarea> prend les mêmes.
          <textarea rows={lignes} ref={ref as Ref<HTMLTextAreaElement>} {...(reste as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)} {...communs} />
        ) : (
          <input ref={ref as Ref<HTMLInputElement>} {...reste} {...communs} />
        )}
        {suffixe !== undefined && <span className="shrink-0 text-faint">{suffixe}</span>}
      </div>
      <SousLeChamp id={idAide} aide={aide} erreur={erreur} />
    </div>
  );
}
